import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSiteData } from '@/contexts/SiteDataContext';
import OrgInfoSection from './sections/OrgInfoSection';
import ContactSection from './sections/ContactSection';
import HeroSlidesSection from './sections/HeroSlidesSection';
import ImpactSection from './sections/ImpactSection';
import GalleryManageSection from './sections/GalleryManageSection';
import ProgramsSection from './sections/ProgramsSection';
import FaqSection from './sections/FaqSection';
import TeamSection from './sections/TeamSection';
import SocialLinksSection from './sections/SocialLinksSection';
import CampaignsSection from './sections/CampaignsSection';

const ADMIN_PASSWORD = 'happyfaces2026';
const PASS_KEY = 'hf_admin_auth';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { id: 'org', label: 'Organization Info', icon: 'ri-building-2-line' },
  { id: 'contact', label: 'Contact Details', icon: 'ri-contacts-line' },
  { id: 'hero', label: 'Hero Slideshow', icon: 'ri-image-line' },
  { id: 'impact', label: 'Impact Counters', icon: 'ri-bar-chart-box-line' },
  { id: 'campaigns', label: 'Campaigns', icon: 'ri-heart-line' },
  { id: 'gallery', label: 'Gallery Images', icon: 'ri-gallery-line' },
  { id: 'programs', label: 'Programs', icon: 'ri-apps-line' },
  { id: 'team', label: 'Team Members', icon: 'ri-team-line' },
  { id: 'faq', label: 'FAQ', icon: 'ri-question-answer-line' },
  { id: 'social', label: 'Social Media', icon: 'ri-share-line' },
];

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) {
      localStorage.setItem(PASS_KEY, '1');
      onLogin();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-950 via-primary-900 to-foreground-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-accent-500 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-keyhole-line text-white text-2xl"></i>
          </div>
          <h1 className="text-white text-2xl font-bold font-heading">Admin Panel</h1>
          <p className="text-white/50 text-sm mt-1">Happy Faces Hyderabad</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/15 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-white/80 mb-2">Admin Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Enter password"
                autoFocus
                className={`w-full px-4 py-3 pr-10 rounded-xl bg-white/10 border text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 transition-all ${error ? 'border-rose-400 ring-2 ring-rose-400/50' : 'border-white/20'}`}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 cursor-pointer">
                <i className={showPw ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>
            {error && <p className="text-rose-400 text-xs mt-1.5">Incorrect password. Try again.</p>}
          </div>

          <button type="submit" className="w-full py-3 bg-accent-500 hover:bg-accent-400 text-white font-bold rounded-xl transition-colors text-sm whitespace-nowrap">
            <i className="ri-login-box-line mr-2"></i>Enter Admin Panel
          </button>
        </form>

        <p className="text-center text-white/30 text-xs mt-4">Default password: <code className="text-white/50">happyfaces2026</code></p>

        <div className="text-center mt-6">
          <Link to="/" className="text-white/40 hover:text-white/70 text-xs transition-colors">
            <i className="ri-arrow-left-line mr-1"></i>Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { data, resetAll } = useSiteData();
  const [confirmReset, setConfirmReset] = useState(false);

  const stats = [
    { label: 'Hero Slides', value: data.heroSlides.length, icon: 'ri-image-line', color: 'bg-primary-100 text-primary-600' },
    { label: 'Gallery Photos', value: data.galleryImages.length, icon: 'ri-gallery-line', color: 'bg-accent-100 text-accent-600' },
    { label: 'Campaigns', value: data.campaigns.length, icon: 'ri-heart-line', color: 'bg-secondary-100 text-secondary-600' },
    { label: 'Team Members', value: data.team.length, icon: 'ri-team-line', color: 'bg-emerald-100 text-emerald-600' },
    { label: 'Programs', value: data.programs.length, icon: 'ri-apps-line', color: 'bg-orange-100 text-orange-600' },
    { label: 'FAQs', value: data.faqs.length, icon: 'ri-question-answer-line', color: 'bg-violet-100 text-violet-600' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-foreground-900 mb-1">Dashboard</h2>
        <p className="text-foreground-500 text-sm">Overview of your website content. Use the sidebar to edit any section.</p>
      </div>

      {/* Org quick-view */}
      <div className="flex items-center gap-4 p-5 bg-primary-50 rounded-2xl border border-primary-100">
        <img src={data.org.logoUrl} alt="Logo" className="w-14 h-14 rounded-full object-cover border-2 border-primary-200 flex-shrink-0" />
        <div>
          <h3 className="font-bold text-foreground-900 text-lg">{data.org.name}</h3>
          <p className="text-foreground-600 text-sm">{data.org.tagline}</p>
          <p className="text-accent-600 text-xs mt-0.5 font-medium">Reg. No: {data.org.regNo}</p>
        </div>
      </div>

      {/* Contact quick-view */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 bg-background-100 rounded-xl border border-background-200 flex items-start gap-3">
          <i className="ri-map-pin-line text-primary-500 mt-0.5"></i>
          <div><p className="text-xs text-foreground-500 mb-0.5">Address</p><p className="text-sm text-foreground-800 font-medium leading-snug">{data.contact.address}</p></div>
        </div>
        <div className="p-4 bg-background-100 rounded-xl border border-background-200 flex items-start gap-3">
          <i className="ri-phone-line text-accent-500 mt-0.5"></i>
          <div><p className="text-xs text-foreground-500 mb-0.5">Phone</p><a href={`tel:${data.contact.phone}`} className="text-sm text-foreground-800 font-medium">{data.contact.phone}</a></div>
        </div>
        <div className="p-4 bg-background-100 rounded-xl border border-background-200 flex items-start gap-3">
          <i className="ri-mail-line text-secondary-500 mt-0.5"></i>
          <div><p className="text-xs text-foreground-500 mb-0.5">Email</p><a href={`mailto:${data.contact.email}`} className="text-sm text-foreground-800 font-medium break-all">{data.contact.email}</a></div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="p-5 bg-white rounded-2xl border border-background-200 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl ${s.color} flex items-center justify-center flex-shrink-0`}>
              <i className={`${s.icon} text-xl`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground-900 font-heading">{s.value}</p>
              <p className="text-xs text-foreground-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* View site button */}
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/"
          target="_blank"
          className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-external-link-line"></i>
          View Live Website
        </Link>

        {confirmReset ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-rose-600 font-medium">Reset ALL data to defaults?</span>
            <button onClick={() => { resetAll(); setConfirmReset(false); }} className="px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 transition-colors whitespace-nowrap">Yes, Reset</button>
            <button onClick={() => setConfirmReset(false)} className="px-4 py-2.5 bg-background-200 text-foreground-700 rounded-xl text-sm font-semibold hover:bg-background-300 transition-colors whitespace-nowrap">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setConfirmReset(true)} className="inline-flex items-center gap-2 px-5 py-3 bg-rose-50 text-rose-600 rounded-xl text-sm font-semibold hover:bg-rose-100 transition-colors whitespace-nowrap border border-rose-200">
            <i className="ri-refresh-line"></i>
            Reset to Defaults
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => localStorage.getItem(PASS_KEY) === '1');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { document.title = 'Admin Panel — Happy Faces Hyderabad'; }, []);

  const logout = () => { localStorage.removeItem(PASS_KEY); setAuthed(false); };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  const sectionMap: Record<string, React.ReactNode> = {
    dashboard: <Dashboard />,
    org: <OrgInfoSection />,
    contact: <ContactSection />,
    hero: <HeroSlidesSection />,
    impact: <ImpactSection />,
    campaigns: <CampaignsSection />,
    gallery: <GalleryManageSection />,
    programs: <ProgramsSection />,
    team: <TeamSection />,
    faq: <FaqSection />,
    social: <SocialLinksSection />,
  };

  const activeItem = NAV_ITEMS.find((n) => n.id === activeSection);

  return (
    <div className="min-h-screen bg-background-100 flex">
      {/* Sidebar overlay on mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-primary-950 flex flex-col z-50 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 lg:flex-shrink-0`}>
        {/* Logo area */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-accent-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-settings-3-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-white text-sm font-bold leading-tight">Admin Panel</p>
            <p className="text-white/40 text-[10px]">Happy Faces Hyderabad</p>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-white/50 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-left cursor-pointer ${
                activeSection === item.id
                  ? 'bg-accent-500 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className={`${item.icon} text-base flex-shrink-0`}></i>
              {item.label}
            </button>
          ))}
        </nav>

        {/* Footer actions */}
        <div className="px-3 pb-5 pt-3 border-t border-white/10 space-y-2">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
          >
            <i className="ri-home-line text-base"></i>
            View Website
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-logout-box-line text-base"></i>
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-background-200 px-4 md:px-6 py-4 flex items-center gap-4 sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
            <i className="ri-menu-line text-foreground-700"></i>
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <i className={`${activeItem?.icon || 'ri-dashboard-line'} text-primary-500 text-base flex-shrink-0`}></i>
            <h1 className="text-foreground-900 font-bold text-base truncate">{activeItem?.label || 'Dashboard'}</h1>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:block text-xs text-foreground-400 font-medium bg-background-100 px-3 py-1.5 rounded-full">All changes saved to browser</span>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white rounded-xl text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-eye-line"></i>
              Preview Site
            </Link>
          </div>
        </header>

        {/* Section content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-5xl w-full">
          {sectionMap[activeSection] || <Dashboard />}
        </main>
      </div>
    </div>
  );
}